import {
  CALL_ACTIVITY_STEP_MS,
  CALL_MAX_DURATION_MS,
  CALL_MAX_PARTS,
  CALL_PART_MAX_MS,
  CALL_PART_MIN_MS,
  CALL_PART_RELAXED_MS,
  CALL_SILENCE_MS,
  CALL_SILENCE_RELAXED_MS,
  type CallFinishInput,
  type CallStartInput,
  type CallTrack,
} from "@repo/polaris-shared";
import { PartCutter, levelsToBase64, recorderMimeType } from "./call-support";

/**
 * Records a call made with WhatsApp Desktop (ADR 0067): the microphone (the
 * member) and the system audio shared from the screen (the lead), as three
 * tracks (`mix`, `member`, `contact`) cut in parts of five minutes and uploaded
 * as soon as each part is ready. The volume of the two sources goes with every
 * part: it tells the server who was speaking.
 */

export type CallLead = { id: string; name: string; workspaceSlug: string };

/** Volume bytes of the microphone (`member`) and of the system audio (`contact`). */
export type CallLevels = { member: number; contact: number };

export type CallRecorderPhase = "idle" | "starting" | "recording" | "finishing" | "failed";

export type CallRecorderState = {
  phase: CallRecorderPhase;
  lead: CallLead | null;
  callId: string | null;
  /** `Date.now()` when the recording started. */
  startedAt: number | null;
  /** Parts recorded and not uploaded yet. */
  pendingParts: number;
  /** What went wrong, in Italian, for the person recording. */
  error: string | null;
};

export type CallUploader = {
  start(workspaceSlug: string, leadId: string, input: CallStartInput): Promise<{ id: string }>;
  uploadPart(workspaceSlug: string, callId: string, seq: number, form: FormData): Promise<unknown>;
  finish(workspaceSlug: string, callId: string, input: CallFinishInput): Promise<unknown>;
  cancel(workspaceSlug: string, callId: string): Promise<unknown>;
};

/** The API refused the part for good (closed call, invalid audio): retrying will not help. */
export type CallUploadError = Error & { status?: number };

type PendingPart = {
  seq: number;
  offsetMs: number;
  durationMs: number;
  blobs: Record<CallTrack, Blob>;
  levels: { member: number[]; contact: number[] };
};

type PartRecording = {
  seq: number;
  offsetMs: number;
  startedAt: number;
  recorders: Array<{ track: CallTrack; recorder: MediaRecorder; chunks: Blob[] }>;
  levels: { member: number[]; contact: number[] };
};

type Capture = {
  context: AudioContext;
  streams: MediaStream[];
  destinations: Record<CallTrack, MediaStreamAudioDestinationNode>;
  worklet: AudioWorkletNode;
  mimeType: string;
};

const TRACKS: readonly CallTrack[] = ["mix", "member", "contact"];
/** In `public/`: a file of the app's own origin, as the CSP wants (`script-src 'self'`). */
const WORKLET_URL = "/call-activity-worklet.js";
const RETRY_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 30_000];
/** Shorter than this, the last part is only the click on "Termina". */
const LAST_PART_MIN_MS = 500;

export class CallRecorderError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CallRecorderError";
  }
}

function captureError(error: unknown, step: "microphone" | "screen"): CallRecorderError {
  if (error instanceof CallRecorderError) return error;
  // OverconstrainedError is not always a DOMException: read the name as it comes.
  const name =
    typeof error === "object" && error !== null && "name" in error ? String(error.name) : "";
  if (step === "microphone" && name === "OverconstrainedError") {
    return new CallRecorderError("Il microfono scelto non è più collegato: scegline un altro.");
  }
  if (step === "microphone") {
    return new CallRecorderError(
      name === "NotAllowedError"
        ? "Chrome non ha il permesso del microfono: consentilo dall'icona a sinistra dell'indirizzo e riprova."
        : "Non riusciamo a usare il microfono: controlla che sia collegato e riprova.",
    );
  }
  return new CallRecorderError(
    name === "NotAllowedError"
      ? 'Condivisione annullata. Se non hai visto la finestra di Chrome, su macOS consenti a Chrome "Registrazione schermo e audio di sistema" (Impostazioni di Sistema → Privacy e sicurezza), poi riapri Chrome.'
      : "Non riusciamo a condividere l'audio del Mac: riprova.",
  );
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * How long Chrome and macOS get to give the screen and the microphone (owner,
 * 2026-10-26): a window that never shows must not leave the start spinning.
 * The screen is a choice the person makes in Chrome's window, so it gets longer.
 */
export const SCREEN_TIMEOUT_MS = 45_000;
export const MICROPHONE_TIMEOUT_MS = 20_000;

export const SCREEN_TIMEOUT_MESSAGE =
  "La finestra di Chrome per scegliere lo schermo non è arrivata. Su macOS apri Impostazioni di Sistema → Privacy e sicurezza → Registrazione schermo e audio di sistema e attiva Google Chrome (se è già attivo, toglilo e rimettilo), poi chiudi e riapri Chrome e riprova.";
export const MICROPHONE_TIMEOUT_MESSAGE =
  "Il microfono non ha risposto. In Chrome consentilo dall'icona a sinistra dell'indirizzo; su macOS attiva Google Chrome in Impostazioni di Sistema → Privacy e sicurezza → Microfono. Poi riprova.";

/** "Annulla" while the start waits for Chrome. */
class StartCancelled extends Error {}

function stopStream(stream: MediaStream) {
  for (const track of stream.getTracks()) track.stop();
}

/**
 * Waits for a stream from Chrome for at most `ms`, or until "Annulla". A stream
 * that arrives after either is closed at once: nothing keeps recording.
 */
export function awaitStream(
  request: Promise<MediaStream>,
  options: { ms: number; signal: AbortSignal; timeout: () => Error },
): Promise<MediaStream> {
  return new Promise((resolve, reject) => {
    if (options.signal.aborted) {
      request.then(stopStream, () => undefined);
      reject(new StartCancelled());
      return;
    }
    let settled = false;
    const settle = (done: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      options.signal.removeEventListener("abort", onAbort);
      done();
    };
    const onAbort = () => settle(() => reject(new StartCancelled()));
    const timer = setTimeout(() => settle(() => reject(options.timeout())), options.ms);
    options.signal.addEventListener("abort", onAbort, { once: true });
    request.then(
      (stream) => {
        if (settled) stopStream(stream);
        else settle(() => resolve(stream));
      },
      (error: unknown) => settle(() => reject(error)),
    );
  });
}

export class CallRecorder {
  private state: CallRecorderState = {
    phase: "idle",
    lead: null,
    callId: null,
    startedAt: null,
    pendingParts: 0,
    error: null,
  };
  private readonly listeners = new Set<() => void>();
  private capture: Capture | null = null;
  private part: PartRecording | null = null;
  private nextSeq = 0;
  private endedAt: number | null = null;
  private readonly queue: PendingPart[] = [];
  private draining: Promise<void> | null = null;
  private rotating: Promise<void> | null = null;
  private stopping: Promise<void> | null = null;
  private cutter: PartCutter | null = null;
  /** Volume of the two sources right now, for the meters of the bar. */
  private levels: CallLevels = { member: 0, contact: 0 };
  private readonly levelListeners = new Set<() => void>();
  /** The start waiting for Chrome: "Annulla" stops it. */
  private starting: AbortController | null = null;

  constructor(
    private readonly uploader: CallUploader,
    /** After the call is closed with all its audio uploaded. */
    private readonly onSaved: (lead: CallLead) => void = () => undefined,
  ) {}

  getState = (): CallRecorderState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getLevels = (): CallLevels => this.levels;

  subscribeLevels = (listener: () => void): (() => void) => {
    this.levelListeners.add(listener);
    return () => this.levelListeners.delete(listener);
  };

  private setLevels(levels: CallLevels) {
    this.levels = levels;
    for (const listener of this.levelListeners) listener();
  }

  private set(patch: Partial<CallRecorderState>) {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /**
   * Asks for the screen with its audio, then the microphone, then starts. Call it
   * from a click: the screen is asked before anything is awaited, while Chrome
   * still counts the click. `microphoneId`: the device chosen in the dialog;
   * without it, Chrome's own choice, which may be a microphone that hears nothing.
   */
  async start(lead: CallLead, options: { microphoneId?: string | null } = {}): Promise<void> {
    if (this.state.phase === "starting" || this.state.phase === "recording") return;
    if (this.state.phase === "finishing") {
      throw new CallRecorderError("Aspetta che la chiamata precedente finisca di caricarsi.");
    }
    this.reset();
    this.set({ phase: "starting", lead, error: null });
    const abort = new AbortController();
    this.starting = abort;
    const { signal } = abort;
    // Created during the click, so Chrome lets it play without another gesture.
    const context = new AudioContext();
    const streams: MediaStream[] = [];
    const checkCancelled = () => {
      if (signal.aborted) throw new StartCancelled();
    };
    try {
      const mimeType = recorderMimeType((type) => MediaRecorder.isTypeSupported(type));
      if (!mimeType) throw new CallRecorderError("Questo browser non registra l'audio.");
      const screenRequest = navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          suppressLocalAudioPlayback: false,
        },
        // Chrome's options: offer the whole screen with its audio, not this tab.
        systemAudio: "include",
        selfBrowserSurface: "exclude",
        surfaceSwitching: "exclude",
        monitorTypeSurfaces: "include",
      } as DisplayMediaStreamOptions);
      const screen = await awaitStream(screenRequest, {
        ms: SCREEN_TIMEOUT_MS,
        signal,
        timeout: () => new CallRecorderError(SCREEN_TIMEOUT_MESSAGE),
      }).catch((error: unknown) => {
        throw error instanceof StartCancelled ? error : captureError(error, "screen");
      });
      streams.push(screen);
      const microphone = await awaitStream(
        navigator.mediaDevices.getUserMedia({
          audio: {
            ...(options.microphoneId ? { deviceId: { exact: options.microphoneId } } : {}),
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        }),
        {
          ms: MICROPHONE_TIMEOUT_MS,
          signal,
          timeout: () => new CallRecorderError(MICROPHONE_TIMEOUT_MESSAGE),
        },
      ).catch((error: unknown) => {
        throw error instanceof StartCancelled ? error : captureError(error, "microphone");
      });
      streams.push(microphone);
      const system = screen.getAudioTracks();
      if (system.length === 0) {
        throw new CallRecorderError(
          'Manca l\'audio del Mac: riprova scegliendo "Schermo intero" e attivando "Condividi anche l\'audio di sistema".',
        );
      }
      // Only the audio is recorded: the screen as slow and small as Chrome allows.
      for (const track of screen.getVideoTracks()) {
        void track.applyConstraints({ frameRate: 1, width: 320 }).catch(() => undefined);
      }
      await context.audioWorklet.addModule(WORKLET_URL);
      await context.resume();
      checkCancelled();
      const microphoneSource = context.createMediaStreamSource(microphone);
      const systemSource = context.createMediaStreamSource(new MediaStream(system));
      const destination = () => {
        const node = context.createMediaStreamDestination();
        node.channelCount = 1;
        node.channelCountMode = "explicit";
        return node;
      };
      const destinations = { mix: destination(), member: destination(), contact: destination() };
      microphoneSource.connect(destinations.mix);
      systemSource.connect(destinations.mix);
      microphoneSource.connect(destinations.member);
      systemSource.connect(destinations.contact);
      const worklet = new AudioWorkletNode(context, "call-activity", {
        numberOfInputs: 2,
        numberOfOutputs: 1,
        processorOptions: { stepMs: CALL_ACTIVITY_STEP_MS },
      });
      microphoneSource.connect(worklet, 0, 0);
      systemSource.connect(worklet, 0, 1);
      // Never to the speakers: silent, it only keeps the measuring running.
      const silent = context.createGain();
      silent.gain.value = 0;
      worklet.connect(silent).connect(context.destination);
      const call = await this.uploader.start(lead.workspaceSlug, lead.id, {
        microphone: microphone.getAudioTracks()[0]?.label || null,
      });
      if (signal.aborted) {
        // "Annulla" while the call was being opened: nothing stays on the server.
        void this.uploader.cancel(lead.workspaceSlug, call.id).catch(() => undefined);
        throw new StartCancelled();
      }
      this.starting = null;
      this.capture = { context, streams, destinations, worklet, mimeType };
      worklet.port.onmessage = (event: MessageEvent<[number, number]>) => this.onLevels(event.data);
      // "Interrompi condivisione" in Chrome, or the microphone unplugged: the call is over.
      for (const track of [...system, ...microphone.getAudioTracks()]) {
        track.addEventListener("ended", () => void this.stop());
      }
      this.cutter = new PartCutter({
        stepMs: CALL_ACTIVITY_STEP_MS,
        minMs: CALL_PART_MIN_MS,
        relaxedMs: CALL_PART_RELAXED_MS,
        maxMs: CALL_PART_MAX_MS,
        silenceMs: CALL_SILENCE_MS,
        relaxedSilenceMs: CALL_SILENCE_RELAXED_MS,
      });
      this.set({ phase: "recording", callId: call.id, startedAt: Date.now() });
      this.beginPart();
    } catch (error) {
      for (const stream of streams) stopStream(stream);
      void context.close().catch(() => undefined);
      if (this.starting === abort) this.starting = null;
      // A newer start or "Annulla" already set the state.
      if (signal.aborted) return;
      this.set({
        phase: "idle",
        lead: null,
        error:
          error instanceof CallRecorderError
            ? error.message
            : "La registrazione non è partita: riprova tra qualche secondo.",
      });
    }
  }

  /** "Termina": closes the last part, uploads what is left and closes the call. */
  stop(): Promise<void> {
    if (this.state.phase !== "recording") return this.stopping ?? Promise.resolve();
    this.stopping = (async () => {
      this.endedAt = Date.now();
      this.set({ phase: "finishing" });
      const last = await this.closePart(this.part);
      this.release();
      // A part closed by the five minutes just before "Termina" goes first.
      await this.rotating;
      if (last && last.durationMs >= LAST_PART_MIN_MS) this.enqueue(last);
      await this.complete();
    })().finally(() => {
      this.stopping = null;
    });
    return this.stopping;
  }

  /** After a failed upload: tries the parts left again, then closes the call. */
  async retry(): Promise<void> {
    if (this.state.phase !== "failed") return;
    this.set({ phase: "finishing", error: null });
    await this.complete();
  }

  /** After a failed upload: closes the call with the parts already uploaded. */
  async finishWithoutUnsent(): Promise<void> {
    if (this.state.phase !== "failed") return;
    this.queue.length = 0;
    this.set({ phase: "finishing", pendingParts: 0, error: null });
    await this.complete();
  }

  /**
   * "Annulla": nothing is kept, on the server either. While the start waits for
   * Chrome, it stops waiting, closes what Chrome already gave and goes back to
   * the start.
   */
  async cancel(): Promise<void> {
    if (this.state.phase === "starting") {
      this.starting?.abort();
      this.starting = null;
      this.reset();
      return;
    }
    const callId = this.state.callId;
    const slug = this.state.lead?.workspaceSlug;
    if (this.part) {
      for (const { recorder } of this.part.recorders) {
        if (recorder.state !== "inactive") recorder.stop();
      }
      this.part = null;
    }
    this.release();
    this.queue.length = 0;
    this.reset();
    if (callId && slug) await this.uploader.cancel(slug, callId).catch(() => undefined);
  }

  /** The audio not uploaded, to save on the computer when the upload keeps failing. */
  unsentAudio(): Array<{ name: string; blob: Blob }> {
    return this.queue.map((part) => ({
      name: `chiamata-${(this.state.lead?.name ?? "lead").replace(/[^\p{L}\p{N}]+/gu, "-")}-${part.seq + 1}.webm`,
      blob: part.blobs.mix,
    }));
  }

  private reset() {
    this.part = null;
    this.nextSeq = 0;
    this.endedAt = null;
    this.queue.length = 0;
    this.set({
      phase: "idle",
      lead: null,
      callId: null,
      startedAt: null,
      pendingParts: 0,
      error: null,
    });
  }

  private async complete() {
    await this.drain();
    const callId = this.state.callId;
    const slug = this.state.lead?.workspaceSlug;
    if (!callId || !slug) return;
    if (this.queue.length > 0) {
      this.set({
        phase: "failed",
        error: "Una parte dell'audio non si è caricata. Controlla la connessione e riprova.",
      });
      return;
    }
    try {
      await this.uploader.finish(slug, callId, {
        durationMs: Math.max(
          0,
          (this.endedAt ?? Date.now()) - (this.state.startedAt ?? Date.now()),
        ),
        parts: this.nextSeq,
      });
      const lead = this.state.lead;
      this.reset();
      if (lead) this.onSaved(lead);
    } catch {
      this.set({ phase: "failed", error: "Non riusciamo a chiudere la registrazione: riprova." });
    }
  }

  private onLevels([member, contact]: [number, number]) {
    this.setLevels({ member, contact });
    const part = this.part;
    if (!part) return;
    part.levels.member.push(member);
    part.levels.contact.push(contact);
    if (!this.cutter?.push(member, contact)) return;
    const elapsed = Date.now() - (this.state.startedAt ?? Date.now());
    // Four hours: the longest call Polaris takes.
    if (elapsed >= CALL_MAX_DURATION_MS || this.nextSeq >= CALL_MAX_PARTS) void this.stop();
    else this.rotate();
  }

  private beginPart() {
    const capture = this.capture;
    const startedAt = this.state.startedAt;
    if (!capture || startedAt === null) return;
    const recorders = TRACKS.map((track) => {
      const recorder = new MediaRecorder(capture.destinations[track].stream, {
        mimeType: capture.mimeType,
        audioBitsPerSecond: 32_000,
      });
      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.start();
      return { track, recorder, chunks };
    });
    this.part = {
      seq: this.nextSeq,
      offsetMs: Date.now() - startedAt,
      startedAt: Date.now(),
      recorders,
      levels: { member: [], contact: [] },
    };
    this.nextSeq += 1;
  }

  private async closePart(part: PartRecording | null): Promise<PendingPart | null> {
    const capture = this.capture;
    if (this.part === part) this.part = null;
    if (!part || !capture) return null;
    await Promise.all(
      part.recorders.map(
        ({ recorder }) =>
          new Promise<void>((resolve) => {
            if (recorder.state === "inactive") return resolve();
            recorder.addEventListener("stop", () => resolve(), { once: true });
            recorder.stop();
          }),
      ),
    );
    const type = capture.mimeType.split(";")[0] ?? "audio/webm";
    const blob = (track: CallTrack) =>
      new Blob(part.recorders.find((item) => item.track === track)?.chunks ?? [], { type });
    return {
      seq: part.seq,
      offsetMs: part.offsetMs,
      durationMs: Date.now() - part.startedAt,
      blobs: { mix: blob("mix"), member: blob("member"), contact: blob("contact") },
      levels: part.levels,
    };
  }

  /** Ends the part during a silence of both voices (`PartCutter`) and starts the next. */
  private rotate() {
    const previous = this.part;
    if (!previous) return;
    // The next part starts before the previous one stops: not even a few
    // milliseconds of the silence are lost between them.
    this.beginPart();
    this.cutter?.restart();
    this.rotating = this.closePart(previous)
      .then((done) => {
        if (done) this.enqueue(done);
      })
      .finally(() => {
        this.rotating = null;
      });
  }

  private release() {
    const capture = this.capture;
    this.capture = null;
    if (!capture) return;
    capture.worklet.port.onmessage = null;
    this.setLevels({ member: 0, contact: 0 });
    for (const stream of capture.streams) for (const track of stream.getTracks()) track.stop();
    void capture.context.close().catch(() => undefined);
  }

  private enqueue(part: PendingPart) {
    this.queue.push(part);
    this.set({ pendingParts: this.queue.length });
    void this.drain();
  }

  private drain(): Promise<void> {
    if (this.draining) return this.draining;
    this.draining = (async () => {
      while (this.queue.length > 0) {
        const part = this.queue[0];
        const callId = this.state.callId;
        const slug = this.state.lead?.workspaceSlug;
        if (!part || !callId || !slug) break;
        if (!(await this.upload(slug, callId, part))) break;
        this.queue.shift();
        this.set({ pendingParts: this.queue.length });
      }
    })().finally(() => {
      this.draining = null;
    });
    return this.draining;
  }

  private async upload(slug: string, callId: string, part: PendingPart): Promise<boolean> {
    const form = new FormData();
    form.append(
      "meta",
      JSON.stringify({
        offsetMs: part.offsetMs,
        durationMs: part.durationMs,
        activity: {
          member: { stepMs: CALL_ACTIVITY_STEP_MS, levels: levelsToBase64(part.levels.member) },
          contact: { stepMs: CALL_ACTIVITY_STEP_MS, levels: levelsToBase64(part.levels.contact) },
        },
      }),
    );
    for (const track of TRACKS) form.append(track, part.blobs[track], `${part.seq}-${track}.webm`);
    for (let attempt = 0; ; attempt += 1) {
      try {
        await this.uploader.uploadPart(slug, callId, part.seq, form);
        return true;
      } catch (error) {
        const status = (error as CallUploadError).status ?? 0;
        // 4xx other than a timeout or a rate limit will answer the same next time.
        const final = status >= 400 && status < 500 && status !== 408 && status !== 429;
        const delay = RETRY_DELAYS_MS[attempt];
        if (final || delay === undefined) return false;
        await wait(delay);
      }
    }
  }
}
